import React, { useContext, useLayoutEffect } from "react";
import Container from "Screens/Container";
import { AppContext } from "context/App";
import PrimaryButton from "components/Buttons/PrimaryButton";
import { packClasses } from "Util";
import { useAuth } from "hooks/Auth";
import { useHistory, Link } from "react-router-dom";
import { ArrowRightIcon } from "@heroicons/react/solid";
import { CubeIcon } from "@heroicons/react/outline";

export default function DashboardScreen(props) {
    const { setCurrentNav } = useContext(AppContext);
    const history = useHistory();
    const auth = useAuth();
    useLayoutEffect(() => {
        setCurrentNav("home");
    }, [setCurrentNav]);

    return (
        <Container>
            <div className="max-w-4xl mx-auto space-y-4">
                <h1 className="text-3xl text-gray-800">SNworks Accounts</h1>

                <div className="grid grid-cols-10 gap-0 md:gap-4">
                    <div className="col-span-10 md:col-span-4 space-y-4 order-2 md:order-1 mt-4 md:mt-0">
                        <div className="bg-white rounded-md shadow-sm p-4 space-y-4">
                            <p className="prose-sm">
                                SNworks Account is your gateway to CEO, Guides
                                and other SNworks products.
                            </p>
                            {auth.user.is_admin && (
                                <div
                                    className={packClasses(
                                        "flex justify-around",
                                        !auth.user.is_admin &&
                                            !auth.user.is_snworks
                                            ? "hidden"
                                            : ""
                                    )}
                                >
                                    <PrimaryButton
                                        type="button"
                                        onClick={() =>
                                            history.push("/app/user")
                                        }
                                    >
                                        Manage Users
                                    </PrimaryButton>
                                    <PrimaryButton
                                        type="button"
                                        onClick={() =>
                                            history.push("/app/group")
                                        }
                                    >
                                        Manage Groups
                                    </PrimaryButton>
                                </div>
                            )}
                        </div>

                        <div className="bg-white rounded-md shadow-sm p-4 space-y-4 text-sm">
                            <h2 className="text-xl text-gray-800">Help!</h2>

                            <p className="prose-sm">
                                Our normal support hours are Monday through
                                Friday from 9 a.m. to 5 p.m. ET. During that
                                time we're available either by phone at
                                (517)&nbsp;435-3145 email at{" "}
                                <a
                                    href="mailto:support@getsnworks.com"
                                    className="text-green-600 underline"
                                >
                                    support@getsnworks.com
                                </a>
                                .
                            </p>

                            <p className="prose-sm">
                                After-hours emergency support is available by
                                phone at (517)&nbsp;435-3145. Email is not
                                monitored outside of normal support hours.
                            </p>

                            <p className="prose-sm">
                                The most up-to-date status of all our systems
                                can be found at{" "}
                                <a
                                    href="https://uptime.getsnworks.com"
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="text-green-600 underline"
                                >
                                    uptime.getsnworks.com
                                </a>
                                .
                            </p>
                        </div>

                        <div className="bg-white rounded-md shadow-sm p-4 space-y-4 text-sm">
                            <h2 className="text-xl text-gray-800">Billing</h2>
                            <p className="prose-sm">
                                Invoices will be sent to the following person:
                            </p>
                            <div className="grid grid-cols-3 gap-2 text-sm text-gray-800">
                                <div className="col-span-1">Name:</div>
                                <div className="col-span-2 font-semibold">
                                    {auth.publication.billing_name}
                                </div>
                                <div className="col-span-1">Contact:</div>
                                <div className="col-span-2 font-semibold">
                                    {auth.publication.billing_contact}
                                </div>
                            </div>
                            <div>
                                <Link
                                    to="/app/publication"
                                    className="text-green-600 underline"
                                >
                                    Update billing info{" "}
                                    <ArrowRightIcon className="h-4 w-4 inline" />
                                </Link>
                            </div>
                            <p className="prose-sm">
                                For billing questions, please contact us at
                                (517)&nbsp;295-1680 or{" "}
                                <a
                                    href="mailto:support@getsnworks.com"
                                    className="text-green-600 underline"
                                >
                                    support@getsnworks.com
                                </a>
                                .
                            </p>
                        </div>
                    </div>
                    <div className="col-span-12 md:col-span-6 space-y-4 order-1 md:order-2">
                        <div>
                            <div
                                className="bg-white rounded-md shadow-sm p-4 flex space-x-4 cursor-pointer hover:shadow-lg"
                                onClick={() => {
                                    window.open(
                                        process.env.REACT_APP_CEO3_DOMAIN,
                                        "_blank"
                                    );
                                }}
                            >
                                <div className="bg-green-600 rounded-lg p-1">
                                    <CubeIcon className="h-10 w-10 text-white" />
                                </div>
                                <div className="text-gray-800 text-sm">
                                    <div className="text-lg">CEO 3</div>
                                    <div className="text-sm">
                                        Next-gen content management.
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div
                                className="bg-white rounded-md shadow-sm p-4 flex space-x-4 cursor-pointer hover:shadow-lg"
                                onClick={() => {
                                    window.open(
                                        process.env.REACT_APP_ROOT_API.replace(
                                            "{client_code}",
                                            auth.publication.client_code
                                        ),
                                        "_blank"
                                    );
                                }}
                            >
                                <div className="bg-blue-500 rounded-lg p-1">
                                    <CubeIcon className="h-10 w-10 text-white" />
                                </div>
                                <div className="text-gray-800 text-sm">
                                    <div className="text-lg">CEO 2</div>
                                    <div className="text-sm">
                                        Current, stable version of CEO.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-md shadow-sm p-4 space-y-4 text-sm">
                            <h2 className="text-xl text-gray-800">
                                Updates from SNworks
                            </h2>
                            <iframe
                                className="w-full overflow-scroll"
                                style={{ height: "50vh" }}
                                src="/assets/changelog/index.html"
                                title="Changelog"
                            ></iframe>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    );
}
